import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CellClickedEvent, GridApi, GridReadyEvent, ICellRendererParams } from '@ag-grid-community/core';
import { debounceTime, distinctUntilChanged, fromEvent, Subscription } from 'rxjs';
import { FieldExpression, LuceneExpression, term, toString as luceneToString } from 'lucene';
import { AgGridModule } from '@ag-grid-community/angular';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgClass } from '@angular/common';

import { pageTransitions } from '../../../../../../../shared/animations/page-transitions';
import { TranslationService } from '../../../../../../../shared/services/translation.service';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../../../../shared/datacenter/grid-options';
import { CommonColDef, RadioBtnColDef } from '../../../../../../../shared/datacenter/column-defs';
import { OrderBy, OrderByDirection } from '../../../../../../../shared/utils/constant';
import { Users } from '../../../../../../../users/models/user';
import { AddSubOrganizationService } from '../../../../../../services/add-suborg.service';
import { AddSubOrgPageContent } from '../../../../../../../shared/models/add-suborg-info';
import { OrganizationUserInfo } from '../../../../../../models/organization-user-info';
import { OrganizationDetails } from '../../../../../../models/organization';
import { LoadingSpinnerComponent } from '../../../../../../../shared/components/utils/loading-spinner.component';

const resizeObservable = fromEvent(globalThis, 'resize');
@Component({
	selector: 'copo-step-choose-administrator',
	templateUrl: './step-choose-administrator.component.html',
	styleUrl: './step-choose-administrator.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule,
		MatIconModule,
		TranslateModule,
		AgGridModule,
		NgClass,
		LoadingSpinnerComponent,
	],
})
export class StepChooseAdministartorComponent extends AddSubOrgPageContent implements OnDestroy, OnInit {
	@HostBinding('class.passthrough') readonly passthrough = true;
	resizeSub?: Subscription;
	sub?: Subscription;
	orgUsersServiceSub?: Subscription;
	fistName?: string;
	page = 1;
	limit = 10_000;
	sortWay = OrderByDirection.ASC;
	sortBy: keyof Users = 'firstName';
	sortWayOrgUsers = OrderByDirection.DESC;
	sortByOrgUsers: keyof OrganizationUserInfo = 'id';

	lastName?: string;
	email?: string;
	orgId = '';
	userList?: Users[];
	orgUserList?: OrganizationUserInfo[];
	isLoading = false;
	selectedRow?: Users;
	nextClicked = false;
	isAnyOneUserSelected = false;
	selectedUser?: Users;
	selectedOrg?: OrganizationDetails;
	gridApi?: GridApi;
	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription: Subscription;
	isSearchClick = false;
	quickSearchFilter?: LuceneExpression;
	newSubOrg = false;
	readonly modules = clientSideAgGridModules;
	fields = [
		'id',
		'firstName',
		'lastName',
		'address',
		'personMembers.role',
		'personMembers.status'
	];

	gridOptions: CommonGridOptions<Users | OrganizationUserInfo>;

	constructor(
		private readonly translationService: TranslationService,
		private readonly addSubOrgService: AddSubOrganizationService
	) {
		super();
		this.gridOptions = new CommonGridOptions<Users | OrganizationUserInfo>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new CommonColDef<Users | OrganizationUserInfo>({
					field: 'id',
					hide: true
				}),
				new RadioBtnColDef<Users | OrganizationUserInfo>({
					headerName: '',
					colId: 'radioBtn',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					pinned: 'left',
					hide: this.hide(),
					onCellClicked: event => {
						this.onUserSelection(event);
						event.api.refreshCells({ columns: [event.column] });
					},
					cellRendererParams: {
						disabled: false,
						isChecked: (params: ICellRendererParams<Users, unknown>) =>
							Boolean(Number(params.data?.id) === this.selectedUser?.id)
					},
				}),

				new CommonColDef<Users | OrganizationUserInfo>({
					headerName: this.translationService.getTR('add-user.name'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const userData = params.data;
						return `${ userData.firstName }  ${ userData.lastName }`;
					},
					fields: ['firstName', 'lastName'],
					width: 30
				}),
				new CommonColDef<Users | OrganizationUserInfo>({
					headerName: this.translationService.getTR('add-user.email'),
					field: 'address.email',
				}),
			]
		});
		this.sub = this.addSubOrgService.isAnyOneUserSelected.subscribe(isAnyOneUserSelected => {
			this.isAnyOneUserSelected = isAnyOneUserSelected;
		});
		this.sub = this.addSubOrgService.isNewSubOrg.subscribe(isNewSubOrg => {
			this.newSubOrg = isNewSubOrg;
		});
		this.sub = this.addSubOrgService.currentSelectedUser
			.subscribe(data => {
				this.selectedUser = data;
			});
		this.sub = this.addSubOrgService.currentSelectedOrg
			.subscribe(data => {
				this.selectedOrg = data;
			});
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(async searchString => {
			this.isLoading = true;
			if (this.gridApi) {
				if (searchString === '') {
					await this.resetGrid();
					return;
				}
				this.quickSearchFilter = <LuceneExpression> this.createQuickSearchFilter(searchString);
				await this.findUsers();
				this.gridApi.sizeColumnsToFit();
			}
		});
		this.sub = this.addSubOrgService.orgId.subscribe(orgId => {
			this.orgId = orgId;
		});
	}

	ngOnInit() {
		(this.newSubOrg ? this.findUsers() : this.getOrganizationAdminUser())
			.catch((err: unknown) => {
				console.error(err);
			});
	}

	async resetGrid() {
		this.isSearchClick = false;
		if (this.gridApi) {
			this.quickSearchFilter = undefined;
			await this.findUsers();
			this.gridApi.sizeColumnsToFit();
		}
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
		this.searchTextSubscription.unsubscribe();
	}

	onUserSelection(event: CellClickedEvent<Users | OrganizationUserInfo>) {
		if (!event.node.data) {
			return;
		}
		this.addSubOrgService.updateNodeSelectedUsers(Number(event.node.id));
		this.addSubOrgService.updateUserId(String(event.node.data.id));
		this.addSubOrgService.updateFirstName(String(event.node.data.firstName));
		this.addSubOrgService.updateLastName(String(event.node.data.lastName));
		this.addSubOrgService.updateEmail(String(event.node.data.address.email));
		this.addSubOrgService.updateIsAnyOneUserChoiceSelected(true);
		this.selectedRow = <Users>event.node.data;
		this.addSubOrgService.setSelectedUser(this.selectedRow);
	}

	async findUsers() {
		const orderBy: OrderBy<keyof Users>[] = [];
		if (orderBy.length === 0) {
			orderBy.push(new OrderBy(this.sortBy, this.sortWay));
		}
		try {
			await this.addSubOrgService.findUsersForSubOrg(
				['id', 'firstName', 'lastName', 'address.email', 'status'],
				this.createFilter(),
				this.page,
				this.limit,
				new OrderBy(this.sortBy, this.sortWay),
			).then(userList => {
				this.userList = userList.results;
				this.isLoading = false;
			});
		} catch (err) {
			console.error('Error from http put current org detail', err);
		}
	}

	createFilter() {
		const statusField: FieldExpression = {
			field: 'status',
			term: 'Activ',
			quoted: false
		};
		if (this.quickSearchFilter) {
			const conditionNode1 = {
				left: statusField,
				operator: 'AND',
				right: this.quickSearchFilter,
				parenthesized: true
			};
			return luceneToString(conditionNode1);
		}
		return luceneToString(statusField);
	}

	checkErrors(): boolean | undefined {
		this.nextClicked = true;
		return this.newSubOrg ? this.isAnyOneUserSelected : true;
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const firstNameField: FieldExpression = {
			field: 'firstName',
			term: escapedTextFilter,
			quoted: false
		};

		const lastNameField: FieldExpression = {
			field: 'lastName',
			term: escapedTextFilter,
			quoted: false
		};

		return {
			left: firstNameField,
			operator: 'OR',
			right: lastNameField,
			parenthesized: true
		};
	}

	async getOrganizationAdminUser() {
		if (!this.selectedOrg?.id) {
			return;
		}
		await this.addSubOrgService.getAdminUsers(this.selectedOrg.id).then(
			value => {
				this.orgUserList = value;
				// update first value firstname,lastname,email to display on confirmation page
				this.addSubOrgService.updateFirstName(value[0].firstName);
				this.addSubOrgService.updateLastName(value[0].lastName);
				this.addSubOrgService.updateEmail(value[0].address.email);
			}
		);
	}

	hide() {
		this.sub = this.addSubOrgService.isNewSubOrg.subscribe(isNewSubOrg => {
			this.newSubOrg = isNewSubOrg;
		});
		return !this.newSubOrg;
	}
}
